import { useForm } from "react-hook-form";
import {
  TextInput,
  Textarea,
  SegmentedControl,
  Select,
  MultiSelect,
  DateTimePicker,
} from "react-hook-form-mantine";
import "@mantine/dates/styles.css";
import { Ionicon } from "../Ionicon";
import { useState } from "react";
import {
  Paper,
  Button,
  Center,
  Text,
  Select as MantineSelect,
} from "@mantine/core";
import { useUpdateAnnouncement } from "../../api/announcements";
import { modals } from "@mantine/modals";
import dayjs from "dayjs";
import { extractDirtyData } from "../../utils/dirtyData";
import moreThanOneAnnouncementModal from "./MoreThanOneAnnouncementModal";
import LocalizedInput from "../common/LocalizedInput";
import { web_patchAnnouncementSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { mergeI18nWithDefaults } from "../../utils/i18nUtils";
import AnnouncementLinkFields from "./AnnouncementLinkFields";

const EditAnnouncementForm = ({ data, moreThanOneVisible, tags, event }) => {
  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const hasSchedule =
    data.scheduledAt && dayjs(data.scheduledAt).isAfter(dayjs());

  const defaultValues = {
    titleI18n: mergeI18nWithDefaults(
      data.titleI18n,
      mainLanguage,
      otherLanguages
    ),
    textI18n: mergeI18nWithDefaults(
      data.textI18n,
      mainLanguage,
      otherLanguages
    ),
    type: data.type,
    isVisible: data.isVisible ? "true" : "false",
    targetedTagIds: (data.targetedTagIds ?? []).map(String),
    scheduledAt: data.scheduledAt || null,
    linkType: data.linkType ?? "none",
    linkTargetId: data.linkTargetId ? String(data.linkTargetId) : null,
  };

  const tagsOptions = (tags ?? []).map((tag) => ({
    value: String(tag.id),
    label: `${tag.title} - (${tag.usageCount} users)`,
  }));

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues,
    resolver: resolver(web_patchAnnouncementSchema),
  });

  const [schedule, setSchedule] = useState(hasSchedule ? "later" : "now");
  const [pushNotification, setPushNotification] = useState(
    data.shouldAutoNotify ? "automatic" : "manual"
  );

  const { mutate, isPending } = useUpdateAnnouncement(data.id);

  const onSubmit = (data) => {
    if (data.isVisible) {
      data.isVisible = data.isVisible === "true";
    }

    // Handle scheduling
    const initialSchedule = hasSchedule ? "later" : "now";
    const scheduleChanged = schedule !== initialSchedule;

    if (schedule === "later") {
      data.scheduledAt = data.scheduledAt
        ? new Date(data.scheduledAt).toISOString()
        : null;
      data.isVisible = false;
      data.shouldAutoNotify = pushNotification === "automatic";
    } else {
      data.scheduledAt = null;
      data.shouldAutoNotify = false;
    }

    const dirtyData = extractDirtyData(data, dirtyFields);

    let payload = { ...dirtyData };

    // Always include scheduledAt, isVisible, and shouldAutoNotify if schedule was changed
    if (scheduleChanged || dirtyFields.scheduledAt) {
      payload.scheduledAt = data.scheduledAt;
      payload.isVisible = data.isVisible;
      payload.shouldAutoNotify = data.shouldAutoNotify;
    }

    // No tags selected targets all ticket holders
    if ("targetedTagIds" in payload) {
      payload.targetedTagIds = payload.targetedTagIds?.length
        ? payload.targetedTagIds.map(Number)
        : null;
    }

    if (dirtyFields.linkType || dirtyFields.linkTargetId) {
      payload.linkType = data.linkType === "none" ? null : data.linkType;
      payload.linkTargetId = data.linkTargetId
        ? Number(data.linkTargetId)
        : null;
    }

    if (dirtyData.isVisible && moreThanOneVisible) {
      const showBothCallback = () => {
        mutate(payload, {
          onSuccess: () => {
            reset({ ...defaultValues, ...dirtyData });
            modals.closeAll();
          },
        });
      };

      const showOnlyThisCallback = () => {
        mutate(
          { ...payload, isOnlyThisVisible: true },
          {
            onSuccess: () => {
              reset({ ...defaultValues, ...dirtyData });
              modals.closeAll();
            },
          }
        );
      };

      moreThanOneAnnouncementModal(showBothCallback, showOnlyThisCallback);
    } else {
      mutate(payload, {
        onSuccess: reset({ ...defaultValues, ...dirtyData }),
      });
    }
  };

  const localizedInputFields = [
    {
      nameBase: "titleI18n",
      label: "Title",
      component: TextInput,
      props: {
        mt: "sm",
        withAsterisk: true,
        placeholder: "Your title",
      },
    },
    {
      nameBase: "textI18n",
      label: "Text",
      component: Textarea,
      props: {
        rows: 2,
        mt: "sm",
        placeholder: "Enter description text",
      },
    },
  ];

  return (
    <Paper withBorder shadow="md" p={16} mt={16} radius="md">
      <form onSubmit={handleSubmit((data) => onSubmit(data))}>
        <LocalizedInput
          control={control}
          fields={localizedInputFields}
          mainLanguage={mainLanguage}
          otherLanguages={otherLanguages}
          disabled={false}
        />
        <Text size="sm" fw={500} mt="md" style={{ userSelect: "none" }}>
          Appearance
        </Text>
        <SegmentedControl
          name="type"
          control={control}
          data={[
            {
              value: "info",
              label: (
                <Center style={{ gap: 10 }}>
                  <Ionicon name="information-circle-outline" />
                  <span>Info</span>
                </Center>
              ),
            },
            {
              value: "warning",
              label: (
                <Center style={{ gap: 10 }}>
                  <Ionicon name="warning-outline" />
                  <span>Warning</span>
                </Center>
              ),
            },
          ]}
          fullWidth
        />
        <MultiSelect
          label="Targeted announcement"
          description="Leave empty to target all ticket holders"
          placeholder="All ticket holders"
          mt="sm"
          name="targetedTagIds"
          control={control}
          data={tagsOptions}
          searchable
          clearable
        />
        <AnnouncementLinkFields
          control={control}
          watch={watch}
          setValue={setValue}
          eventId={data.eventId}
        />
        <MantineSelect
          label="Schedule"
          mt="sm"
          value={schedule}
          onChange={(value) => setSchedule(value)}
          data={[
            { value: "now", label: "Right now" },
            { value: "later", label: "Schedule for later" },
          ]}
          allowDeselect={false}
        />
        {schedule === "later" && (
          <>
            <DateTimePicker
              control={control}
              minDate={dayjs().format("YYYY-MM-DD")}
              name="scheduledAt"
              label="Scheduled date and time"
              placeholder="Select date and time"
              mt="sm"
              clearable
              value={watch("scheduledAt") ?? null}
              onChange={(date) =>
                setValue("scheduledAt", date ?? null, {
                  shouldDirty: true,
                })
              }
            />
            <MantineSelect
              label="Push notification"
              mt="sm"
              value={pushNotification}
              onChange={(value) => setPushNotification(value)}
              data={[
                { value: "automatic", label: "Automatic" },
                { value: "manual", label: "Manual" },
              ]}
              allowDeselect={false}
            />
            <Text size="xs" c="dimmed" mt={4}>
              {pushNotification === "automatic"
                ? "Push notification for this Announcement will be sent out at the scheduled time."
                : "This Announcement will appear on the homepage of users at scheduled time without the push notification."}
            </Text>
          </>
        )}
        <Select
          label="Visibility"
          placeholder="Select visibility"
          mt="sm"
          name="isVisible"
          control={control}
          data={[
            { label: "Visible", value: "true" },
            { label: "Hidden", value: "false" },
          ]}
          disabled={schedule === "later"}
        />
        <Button
          type="submit"
          mt="sm"
          loading={isPending}
          disabled={!isDirty && schedule === (hasSchedule ? "later" : "now")}
        >
          Edit announcement
        </Button>
      </form>
    </Paper>
  );
};

export default EditAnnouncementForm;
